import {
  createAction,
  createAsyncThunk,
  createSlice,
  PrepareAction,
} from "@reduxjs/toolkit";
import { readUser, persistUser } from "../../services/localStorage.Service";
import {
  User,
  UserModel,
  UserWithProjects,
} from "../../domaine/userModel";
import {
  addUserApi,
  deleteUserApi,
  fetchUsersApi,
  fetchUsersWithProjectsApi,
  updateUserApi,
  UserBody,
} from "../../api/users.api";

export interface UserState {
  user: UserModel | null;
  users: User[];
  usersWithProjects: UserWithProjects[];
  all_projects: any[];
  loading: "idle" | "pending" | "succeeded" | "failed";
}

const initialState: UserState = {
  user: readUser(),
  users: [],
  usersWithProjects: [],
  all_projects: [],
  loading: "idle",
};

const API_URL = "users/";

export const setUser = createAction<PrepareAction<UserModel>>(
  "user/setUser",
  (newUser) => {
    persistUser(newUser);
    return {
      payload: newUser,
    };
  }
);

export const fetchUsers: any = createAsyncThunk(
  "Users/fetchUsers",
  async () => {
    const projects: User[] = await fetchUsersApi(API_URL);
    return projects;
  }
);

export const addUser: any = createAsyncThunk(
  "Users/addUser",
  async (newUser: UserBody, { dispatch }) => {
    const user = await addUserApi(newUser, "users/");
    await dispatch(fetchUsers());
    return user;
  }
);

export const updateUser: any = createAsyncThunk(
  "Users/updateUser",
  async ({ id, newUser }: { id: number; newUser: UserBody }, { dispatch }) => {
    const user = await updateUserApi(id, newUser, `users`);
    await dispatch(fetchUsers());
    return user;
  }
);

export const deleteUser: any = createAsyncThunk(
  "Users/deleteUser",
  async (userId: number, { dispatch }) => {
    const user = deleteUserApi(userId, API_URL);
    dispatch(fetchUsers());
    return user;
  }
);

export const fetchUsersWithProjects: any = createAsyncThunk(
  "Users/fetchUsersWithProjects",
  async () => {
    const users: any[] = await fetchUsersWithProjectsApi(
      "users-with-projects/"
    );
    return users;
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    deleteUsersData: (state) => {
      state.users = [];
      state.loading = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setUser, (state, action) => {
        state.user = action.payload;
      })
      .addCase(fetchUsers.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(fetchUsersWithProjects.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchUsersWithProjects.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.usersWithProjects = action.payload.data;
        state.all_projects = action.payload.all_projects;
      })
      .addCase(fetchUsersWithProjects.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(addUser.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(addUser.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(deleteUser.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(deleteUser.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(updateUser.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(updateUser.rejected, (state) => {
        state.loading = "failed";
      });
  },
});

export const { deleteUsersData } = userSlice.actions;

export default userSlice.reducer;
